import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Box, DialogTitle, DialogContent, DialogActions, Typography, FormHelperText } from '@material-ui/core';

import { AlertWrapper, AutocompleteWrapper, DialogButton } from 'src/components';
import { useFetchStatus } from 'src/hooks';


const getCleanIsic = selectedIsic => Object.fromEntries(
  Object.entries({
    section: selectedIsic.section?.value,
    division: selectedIsic.division?.value,
    group: selectedIsic.group?.value,
    class: selectedIsic.class?.value,
  }).filter(([ , v ]) => v !== '' && v !== undefined),
);

const emptyOption = { label: '', value: '' };
const emptyOptionGroup = { section: [], division: [], group: [], class: [] };

const IsicSelectionDialog = ({ actions }) => {

  const [ selectedIsic, setSelectedIsic ] = useState({
    section: emptyOption,
    division: emptyOption,
    group: emptyOption,
    class: emptyOption,
  });

  const [ isicOptions, setIsicOptions ] = useState({ section: [], division: [], group: [], class: [] });

  const { handleConfirm: fetchIsics } = useFetchStatus({
    confirmFn: async isic => {
      const newOptions = await actions.getIsicOptions(isic);
      setIsicOptions({ ...emptyOptionGroup, ...newOptions });
    },
  });

  useEffect(() => {
    const cleanIsic = getCleanIsic(selectedIsic);
    fetchIsics(cleanIsic); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedIsic.section?.value,
    selectedIsic.division?.value,
    selectedIsic.group?.value,
    selectedIsic.class?.value,
  ]);

  const handleChange = key => (_, selectedOption) => setSelectedIsic(prev => ({ ...prev, [key]: selectedOption }));

  const { fetchStatus: fetchStatusForm, handleConfirm, hasError, errorMessage, hasBadData } = useFetchStatus({
    confirmFn: async () => await actions.saveIsic(getCleanIsic(selectedIsic)),
    closeFn: actions.closeDialog,
    validationFn: () => hasSection && hasDivision && hasGroup,
  });

  const hasSection = Boolean(selectedIsic.section?.value);
  const hasDivision = Boolean(selectedIsic.division?.value);
  const hasGroup = Boolean(selectedIsic.group?.value);

  return (
    <>
      <DialogTitle id="form-dialog-title">
        <span>Seleccionar ISIC</span>
      </DialogTitle>
      <DialogContent>
        <Box p={1}>
          <Box my={2} >
            <Typography variant="body2" required>Sección</Typography>
            <AutocompleteWrapper
              value={selectedIsic.section}
              options={isicOptions.section}
              onChange={handleChange('section')}
              getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
              clearable
            />
            {hasBadData && !hasSection && <FormHelperText error>Debes ingresar una sección</FormHelperText>}
          </Box>
          <Box my={2} >
            <Typography variant="body2" required>División</Typography>
            <AutocompleteWrapper
              value={selectedIsic.division}
              options={isicOptions.division}
              onChange={handleChange('division')}
              getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
              clearable
            />
            {hasBadData && !hasDivision && <FormHelperText error>Debes ingresar una división</FormHelperText>}
          </Box>
          <Box my={2} >
            <Typography variant="body2" required>Grupo</Typography>
            <AutocompleteWrapper
              value={selectedIsic.group}
              options={isicOptions.group}
              onChange={handleChange('group')}
              getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
              clearable
            />
            {hasBadData && !hasGroup && <FormHelperText error>Debes ingresar un grupo</FormHelperText>}
          </Box>
          <Box my={2} >
            <Typography variant="body2" required>Clase</Typography>
            <AutocompleteWrapper
              value={selectedIsic.class}
              options={isicOptions.class}
              onChange={handleChange('class')}
              getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
              clearable
            />
          </Box>
          {hasError && <AlertWrapper severity="error">{errorMessage}</AlertWrapper>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={actions.closeDialog}>Cancelar</Button>
        <DialogButton fetchStatus={fetchStatusForm} onClick={handleConfirm} color='primary'>Confirmar</DialogButton>
      </DialogActions>
    </>
  );
};

IsicSelectionDialog.propTypes = {
  actions: PropTypes.object,
};


export {
  IsicSelectionDialog,
};
